import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  LocalHospital as LocalHospitalIcon,
  Psychology as PsychologyIcon,
  Restaurant as RestaurantIcon,
  MedicalServices as MedicalServicesIcon,
  HealthAndSafety as HealthAndSafetyIcon,
  School as SchoolIcon,
  Group as GroupIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Note as NoteIcon
} from '@mui/icons-material';
import { followUpService } from '../services/followUpService';

interface ViewFollowUpDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  followUpId: number;
}

interface FollowUpDetails {
  id: number;
  patientName: string;
  patientId: string;
  organizationName: string;
  groupName: string;
  followupBy: string;
  notes: string;
  completed: boolean;
  createdAt: string;
  completedDate?: string;
  completedBy?: string;
  completionDetails?: {
    emailedPatient: boolean;
    spokeOnPhone: boolean;
    metInPerson: boolean;
    referralCounselingCenter: boolean;
    referralTeamPhysician: boolean;
    referralNutritionist: boolean;
    referralPrimaryCarePhysician: boolean;
    referralExternalMentalHealthProvider: boolean;
    otherResources: string[];
    patientAcceptedReferral: boolean;
    patientDeclinedReferral: boolean;
    completionNotes: string;
  };
}

const ViewFollowUpDetailsDialog: React.FC<ViewFollowUpDetailsDialogProps> = ({
  open,
  onClose,
  followUpId
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState<FollowUpDetails | null>(null);

  useEffect(() => {
    if (open && followUpId) {
      fetchFollowUpDetails();
    }
  }, [open, followUpId]);

  const fetchFollowUpDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await followUpService.getFollowUpDetail(followUpId);
      setDetails(response);
    } catch (err) {
      setError('Failed to load follow-up details');
      console.error('Error fetching follow-up details:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderCommunicationMethods = () => {
    if (!details?.completionDetails) return null;
    const methods = [];
    
    if (details.completionDetails.emailedPatient) {
      methods.push({ icon: <EmailIcon />, text: 'Emailed Patient' });
    }
    if (details.completionDetails.spokeOnPhone) {
      methods.push({ icon: <PhoneIcon />, text: 'Spoke on Phone' });
    }
    if (details.completionDetails.metInPerson) {
      methods.push({ icon: <PersonIcon />, text: 'Met in Person' });
    }
    
    return methods;
  };

  const renderReferrals = () => {
    if (!details?.completionDetails) return null;
    const referrals = [];
    
    if (details.completionDetails.referralCounselingCenter) {
      referrals.push({ icon: <PsychologyIcon />, text: 'Counseling Center' });
    }
    if (details.completionDetails.referralTeamPhysician) {
      referrals.push({ icon: <LocalHospitalIcon />, text: 'Team Physician' });
    }
    if (details.completionDetails.referralNutritionist) {
      referrals.push({ icon: <RestaurantIcon />, text: 'Nutritionist' });
    }
    if (details.completionDetails.referralPrimaryCarePhysician) {
      referrals.push({ icon: <MedicalServicesIcon />, text: 'Primary Care Physician/External Physician' });
    }
    if (details.completionDetails.referralExternalMentalHealthProvider) {
      referrals.push({ icon: <HealthAndSafetyIcon />, text: 'External Mental Health Provider' });
    }
    
    return referrals;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Follow-Up Information</Typography>
          <Button onClick={onClose} startIcon={<CloseIcon />}>
            Close
          </Button>
        </Box>
      </DialogTitle>
      <Divider />
      
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
        ) : details ? (
          <Box sx={{ py: 2 }}>
            {/* Basic Information */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PersonIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="textSecondary">Patient Name</Typography>
                    <Typography variant="body1">{details.patientName}</Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <SchoolIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="textSecondary">Patient ID</Typography>
                    <Typography variant="body1">{details.patientId}</Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <BusinessIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="textSecondary">Organization</Typography>
                    <Typography variant="body1">{details.organizationName || 'N/A'}</Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <GroupIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="textSecondary">Group</Typography>
                    <Typography variant="body1">{details.groupName || 'N/A'}</Typography>
                  </Box>
                </Box>
              </Grid>
              
              {details.completed && details.completedBy && (
                <>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <PersonIcon color="action" />
                      <Box>
                        <Typography variant="caption" color="textSecondary">Followed up by</Typography>
                        <Typography variant="body1">{details.completedBy}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <CalendarIcon color="action" />
                      <Box>
                        <Typography variant="caption" color="textSecondary">Completion Date</Typography>
                        <Typography variant="body1">
                          {details.completedDate ? formatDate(details.completedDate) : 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>

            {/* Initial Notes */}
            {details.notes && (
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NoteIcon fontSize="small" />
                  Initial Notes
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {details.notes}
                </Typography>
              </Paper>
            )}

            {/* Completion Details */}
            {details.completed && details.completionDetails && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ textAlign: 'center' }}>
                  Tasks Completed
                </Typography>

                <Grid container spacing={3} sx={{ mt: 1 }}>
                  {/* Communication Methods */}
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Communication (Selected)
                      </Typography>
                      <List dense>
                        {renderCommunicationMethods()?.map((method, index) => (
                          <ListItem key={index}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              {method.icon}
                            </ListItemIcon>
                            <ListItemText primary={method.text} />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Grid>

                  {/* Referrals Offered */}
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Referrals Offered
                      </Typography>
                      <List dense>
                        {renderReferrals()?.map((referral, index) => (
                          <ListItem key={index}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              {referral.icon}
                            </ListItemIcon>
                            <ListItemText primary={referral.text} />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Grid>

                  {/* Other Resources & Patient Response */}
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Other Resources Provided
                      </Typography>
                      {details.completionDetails.otherResources.length > 0 ? (
                        <Box sx={{ mb: 2 }}>
                          {details.completionDetails.otherResources.map((resource, index) => (
                            <Chip
                              key={index}
                              label={resource}
                              size="small"
                              sx={{ m: 0.5 }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                          None
                        </Typography>
                      )}
                      
                      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                        Patient Response
                      </Typography>
                      {details.completionDetails.patientAcceptedReferral && (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Patient Accepted Referral"
                          color="success"
                          size="small"
                        />
                      )}
                      {details.completionDetails.patientDeclinedReferral && (
                        <Chip
                          label="Patient Declined Referral"
                          color="error"
                          size="small"
                        />
                      )}
                    </Paper>
                  </Grid>
                </Grid>

                {/* Completion Notes */}
                {details.completionDetails.completionNotes && (
                  <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <NoteIcon fontSize="small" />
                      Completion Notes
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'pre-wrap' }}>
                      {details.completionDetails.completionNotes}
                    </Typography>
                  </Paper>
                )}
              </>
            )}
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default ViewFollowUpDetailsDialog;